import React, { useCallback, useMemo } from 'react';
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { MenuNode, MessageNode, HandoffNode } from './BotNode';

const NODE_TYPE_MAP = {
    menu: MenuNode,
    message: MessageNode,
    handoff: HandoffNode,
};

/* Convert backend flow data to ReactFlow nodes + edges */
export function flowToReactFlow(flow) {
    if (!flow || !flow.nodes) return { nodes: [], edges: [] };

    const rfNodes = flow.nodes.map((node, i) => ({
        id: node.node_id,
        type: node.type || 'menu',
        position: node.position || { x: 250 + (i % 3) * 280, y: Math.floor(i / 3) * 200 },
        data: {
            label: node.label || node.node_id,
            message: node.message || '',
            optionCount: node.options?.length || 0,
            presentation: node.presentation || (node.type === 'menu' ? 'buttons' : 'text'),
            isRoot: node.node_id === flow.root_node_id,
        },
        selected: false,
    }));

    const rfEdges = [];
    flow.nodes.forEach(node => {
        (node.options || []).forEach((opt, i) => {
            if (opt.next_node) {
                rfEdges.push({
                    id: `${node.node_id}-opt${i}-${opt.next_node}`,
                    source: node.node_id,
                    target: opt.next_node,
                    label: opt.label || opt.input,
                    type: 'smoothstep',
                    animated: false,
                    style: { stroke: '#6366f1', strokeWidth: 2 },
                    markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' },
                });
            }
        });
        if (node.fallback_node) {
            rfEdges.push({
                id: `${node.node_id}-fallback-${node.fallback_node}`,
                source: node.node_id,
                target: node.fallback_node,
                label: 'fallback',
                type: 'smoothstep',
                animated: true,
                style: { stroke: '#f59e0b', strokeWidth: 1.5, strokeDasharray: '5 5' },
                markerEnd: { type: MarkerType.ArrowClosed, color: '#f59e0b' },
            });
        }
    });

    return { nodes: rfNodes, edges: rfEdges };
}

/* Sync ReactFlow positions back to the flow data */
export function syncPositions(flow, rfNodes) {
    if (!flow) return flow;
    return {
        ...flow,
        nodes: flow.nodes.map(node => {
            const rfNode = rfNodes.find(n => n.id === node.node_id);
            return rfNode ? { ...node, position: rfNode.position } : node;
        }),
    };
}

export default function FlowCanvas({
    flow,
    selectedNodeId,
    onNodeSelect,
    onNodesChange,
    onAddNode,
}) {
    const nodeTypes = useMemo(() => NODE_TYPE_MAP, []);
    const { nodes: initialNodes, edges: initialEdges } = useMemo(
        () => flowToReactFlow(flow),
        [flow]
    );

    const [nodes, setNodes, onNodesChangeInternal] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChangeInternal] = useEdgesState(initialEdges);

    // Sync when flow changes externally
    React.useEffect(() => {
        const { nodes: newNodes, edges: newEdges } = flowToReactFlow(flow);
        setNodes(prev => {
            // Preserve user-dragged positions
            return newNodes.map(nn => {
                const existing = prev.find(p => p.id === nn.id);
                return existing
                    ? { ...nn, position: existing.position, selected: nn.id === selectedNodeId }
                    : nn;
            });
        });
        setEdges(newEdges);
    }, [flow, selectedNodeId, setNodes, setEdges]);

    const handleNodesChange = useCallback((changes) => {
        onNodesChangeInternal(changes);
        // Report position changes back
        const posChanges = changes.filter(c => c.type === 'position' && c.position);
        if (posChanges.length > 0 && onNodesChange) {
            onNodesChange(posChanges);
        }
    }, [onNodesChangeInternal, onNodesChange]);

    const handleNodeClick = useCallback((_, node) => {
        onNodeSelect(node.id);
    }, [onNodeSelect]);

    const handlePaneClick = useCallback(() => {
        onNodeSelect(null);
    }, [onNodeSelect]);

    return (
        <div className="flow-canvas-container">
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                onNodesChange={handleNodesChange}
                onEdgesChange={onEdgesChangeInternal}
                onNodeClick={handleNodeClick}
                onPaneClick={handlePaneClick}
                fitView
                fitViewOptions={{ padding: 0.3 }}
                minZoom={0.3}
                maxZoom={2}
                deleteKeyCode={null}
                nodesConnectable={false}
                edgesUpdatable={false}
                proOptions={{ hideAttribution: true }}
            >
                <Background color="#94a3b8" gap={20} size={1} />
                <Controls showInteractive={false} />
                <MiniMap
                    nodeColor={(node) => {
                        if (node.data?.isRoot) return '#22c55e';
                        switch (node.type) {
                            case 'menu': return '#6366f1';
                            case 'message': return '#3b82f6';
                            case 'handoff': return '#f59e0b';
                            default: return '#94a3b8';
                        }
                    }}
                    maskColor="rgba(0,0,0,0.08)"
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
                />
            </ReactFlow>
            <button className="canvas-add-btn" onClick={onAddNode}>
                + Add Node
            </button>
        </div>
    );
}
